import { ExternalLink } from "lucide-react";
import BaseDrawer from "@/shared/components/Drawer";
import { ACCOUNTING_DATE_BOUNDS } from "@/shared/configs/accountingDateBounds";
import { Button } from "@/shared/ui/Button";
import { Input } from "@/shared/ui/Input";
import AllocationRoomHint from "./AllocationRoomHint";
import {
  CREDIT_LOCKED_HINT,
  CREDIT_UNITS_LOCKED_HINT,
  DEBIT_LOCKED_HINT,
  DEBIT_UNITS_LOCKED_HINT,
  LOCKED_AMOUNT_INPUT_CLASS,
  formatAmount,
  formatCurrencyField,
  getAmountSideLock,
  normalizeCurrencyTyping,
} from "./utils";
import type { EditableAllocationState } from "./utils";
import type { LedgerAllocation } from "../../types/accounting.types";

type EcommerceAllocationDetailProps = {
  open: boolean
  onClose: () => void
  selectedAllocation: LedgerAllocation | null
  simpleEditorRow: EditableAllocationState | null
  setField: (id: string, field: keyof EditableAllocationState, value: string, fallback: LedgerAllocation) => void
  isSimpleEditorBusy: boolean
  onSave: () => void
  onOpenSettlement?: (settlementId: string) => void
  readOnly?: boolean
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }
  catch {
    return iso;
  }
};

const EcommerceAllocationDetail = ({
  open,
  onClose,
  selectedAllocation,
  simpleEditorRow,
  setField,
  isSimpleEditorBusy,
  onSave,
  onOpenSettlement,
  readOnly,
}: EcommerceAllocationDetailProps) => {
  const { isDebitLocked, isCreditLocked } = getAmountSideLock(simpleEditorRow?.debit, simpleEditorRow?.credit);
  const { isDebitLocked: isUnitsDebitLocked, isCreditLocked: isUnitsCreditLocked } = getAmountSideLock(simpleEditorRow?.stockDebitQuantity, simpleEditorRow?.stockCreditQuantity);
  return (
    <BaseDrawer
      drawerId="DRW-ECOM"
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      isFirstLevel={false}
      onBack={onClose}
      width="893px"
      zIndex={65}
      bodyClassName="[&_*]:select-text"
      title={`🛒 Bon Plans${selectedAllocation?.ecommerceOrderRef ? ` — ${selectedAllocation.ecommerceOrderRef}` : ""}`}
      description="Affectation liée à une commande Bon Plans."
    >
      {/* Order info section */}
      {selectedAllocation && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3 mb-4 space-y-3">
          <div className="text-[12px] font-medium text-emerald-900">Informations commande</div>

          {/* Identification */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.ecommerceOrderRef && (
              <div>
                <span className="text-emerald-700">Référence :</span>
                {" "}
                <span className="font-medium text-emerald-950">{selectedAllocation.ecommerceOrderRef}</span>
              </div>
            )}
            {selectedAllocation.ecommerceOrderCreatedAt && (
              <div>
                <span className="text-emerald-700">Date commande :</span>
                {" "}
                <span className="font-medium text-emerald-950">{formatDate(selectedAllocation.ecommerceOrderCreatedAt)}</span>
              </div>
            )}
            {selectedAllocation.ecommerceOrderClientName && (
              <div>
                <span className="text-emerald-700">Bénéficiaire :</span>
                {" "}
                <span className="font-medium text-emerald-950">{selectedAllocation.ecommerceOrderClientName}</span>
              </div>
            )}
            {selectedAllocation.ecommerceOrderClientSite && (
              <div>
                <span className="text-emerald-700">Site :</span>
                {" "}
                <span className="font-medium text-emerald-950">{selectedAllocation.ecommerceOrderClientSite}</span>
              </div>
            )}
            {selectedAllocation.ecommerceOrderStatusName && (
              <div className="md:col-span-2">
                <span className="text-emerald-700">Statut :</span>
                {" "}
                <span className="inline-flex items-center rounded-full bg-emerald-200/70 px-2 py-0.5 text-[11px] font-medium text-emerald-900 ml-1">
                  {selectedAllocation.ecommerceOrderStatusName}
                </span>
              </div>
            )}
          </div>

          {/* Amounts */}
          {(selectedAllocation.ecommerceOrderTotal !== null || selectedAllocation.ecommerceOrderSubsidyUsed !== null || selectedAllocation.ecommerceOrderAllocationBalance !== null) && (
            <div className="border-t border-emerald-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.ecommerceOrderTotal !== null && (
                <div>
                  <span className="text-emerald-700">Total :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-emerald-950">
                    {formatAmount(selectedAllocation.ecommerceOrderTotal)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
              {selectedAllocation.ecommerceOrderSubsidyUsed !== null && (
                <div>
                  <span className="text-emerald-700">Subvention utilisée :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-emerald-950">
                    {formatAmount(selectedAllocation.ecommerceOrderSubsidyUsed)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
              {selectedAllocation.ecommerceOrderTotalAfterSubsidy !== null && (
                <div>
                  <span className="text-emerald-700">Total après subvention :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-emerald-950">
                    {formatAmount(selectedAllocation.ecommerceOrderTotalAfterSubsidy)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
              {selectedAllocation.ecommerceOrderAllocationBalance !== null && (
                <div>
                  <span className="text-emerald-700">Solde affectation :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-emerald-950">
                    {formatAmount(selectedAllocation.ecommerceOrderAllocationBalance)}
                    {" "}
                    &euro;
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settlement info section */}
      {selectedAllocation?.settlementId && (
        <div className="rounded-md border border-indigo-200 bg-indigo-50/60 p-3 mb-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-medium text-indigo-900">Règlement associé</div>
            {onOpenSettlement && (
              <button
                type="button"
                onClick={() => onOpenSettlement(selectedAllocation.settlementId!)}
                className="inline-flex items-center gap-1 text-[11px] text-indigo-600 hover:text-indigo-800 underline underline-offset-2"
              >
                Voir le règlement
                <ExternalLink className="h-3 w-3" />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
            {selectedAllocation.settlementNumber && (
              <div>
                <span className="text-indigo-700">N° :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementNumber}</span>
              </div>
            )}
            {selectedAllocation.settlementDate && (
              <div>
                <span className="text-indigo-700">Date :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDate)}</span>
              </div>
            )}
            {selectedAllocation.settlementIssuer && (
              <div>
                <span className="text-indigo-700">Émetteur :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementIssuer}</span>
              </div>
            )}
            {selectedAllocation.settlementPaymentMethod && (
              <div>
                <span className="text-indigo-700">Support :</span>
                {" "}
                <span className="font-medium text-indigo-950">{selectedAllocation.settlementPaymentMethod}</span>
              </div>
            )}
            {selectedAllocation.settlementDeferredDate && (
              <div>
                <span className="text-indigo-700">Date différée :</span>
                {" "}
                <span className="font-medium text-indigo-950">{formatDate(selectedAllocation.settlementDeferredDate)}</span>
              </div>
            )}
          </div>
          {(selectedAllocation.settlementAmount !== null || selectedAllocation.settlementRemainingAmount !== null) && (
            <div className="border-t border-indigo-200 pt-2 grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1.5 text-[12px]">
              {selectedAllocation.settlementAmount !== null && (
                <div>
                  <span className="text-indigo-700">Montant :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementAmount)}
                    {" "}
                    €
                  </span>
                </div>
              )}
              {selectedAllocation.settlementRemainingAmount !== null && (
                <div>
                  <span className="text-indigo-700">Solde :</span>
                  {" "}
                  <span className="font-medium tabular-nums text-indigo-950">
                    {formatAmount(selectedAllocation.settlementRemainingAmount)}
                    {" "}
                    €
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Operations section */}
      {selectedAllocation && selectedAllocation.ecommerceOrderOperations.length > 0 && (
        <div className="rounded-md border border-slate-200 bg-white p-3 mb-4 space-y-3 text-[12px]">
          <div className="font-medium text-slate-700">Opérations de la commande</div>
          {selectedAllocation.ecommerceOrderOperations.map((op) => {
            const isParentOp = String(op.id) === String(selectedAllocation.activityOperationId);
            const siblingAllocations = op.allocations.filter((a) => a.id !== selectedAllocation.id);
            const hasStock = (op.stockDebitQuantity ?? 0) > 0 || (op.stockCreditQuantity ?? 0) > 0 || (op.stockBalanceQuantity ?? 0) !== 0;
            return (
              <div
                key={op.id}
                className={`rounded-md border p-2 space-y-1.5 ${isParentOp ? "border-emerald-300 bg-emerald-50/60" : "border-slate-100 bg-slate-50/50"}`}
              >
                <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                  <span className={`font-medium ${isParentOp ? "text-emerald-900" : "text-slate-800"}`}>
                    {op.label || `AO${op.id}`}
                  </span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${isParentOp ? "bg-emerald-200/70 text-emerald-900" : "bg-slate-200 text-slate-700"}`}>
                    {op.status || "—"}
                  </span>
                  {op.category && (
                    <span className="text-[10px] text-slate-400">{op.category}</span>
                  )}
                </div>

                {/* Reference info */}
                {(op.familyName || op.referenceName) && (
                  <div className={`text-[11px] ${isParentOp ? "text-emerald-700" : "text-slate-500"}`}>
                    {[op.familyName, op.referenceName].filter(Boolean).join(" / ")}
                    {op.beneficiary ? ` — ${op.beneficiary}` : ""}
                  </div>
                )}

                {/* Stock info */}
                {hasStock && (
                  <div className={`text-[11px] ${isParentOp ? "text-emerald-700" : "text-slate-500"}`}>
                    Stock : Débit
                    {" "}
                    {op.stockDebitQuantity ?? 0}
                    {" "}
                    — Crédit
                    {" "}
                    {op.stockCreditQuantity ?? 0}
                    {" "}
                    — Solde
                    {" "}
                    {op.stockBalanceQuantity ?? 0}
                    {" "}
                    unité(s)
                  </div>
                )}

                {/* Financial balance */}
                <div className={`text-[11px] ${isParentOp ? "text-emerald-700" : "text-slate-500"}`}>
                  Solde :
                  {" "}
                  {formatAmount(op.allocationBalance ?? 0)}
                  {" "}
                  &euro; sur
                  {" "}
                  {formatAmount((op.debit ?? 0) + (op.credit ?? 0))}
                  {" "}
                  &euro;
                </div>

                {/* Allocations table */}
                {(isParentOp ? siblingAllocations : op.allocations).length > 0 && (
                  <table className="w-full text-left text-[11px]">
                    <thead>
                      <tr className={`border-b ${isParentOp ? "border-emerald-200 text-emerald-700" : "border-slate-200 text-slate-500"}`}>
                        <th className="py-1 pr-2 font-medium whitespace-nowrap w-[90px]">Date</th>
                        <th className="py-1 pr-2 font-medium">Libellé</th>
                        <th className="py-1 pr-2 font-medium whitespace-nowrap w-[50px]">Type</th>
                        <th className="py-1 pr-2 font-medium text-right whitespace-nowrap w-[70px]">Débit</th>
                        <th className="py-1 font-medium text-right whitespace-nowrap w-[70px]">Crédit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(isParentOp ? siblingAllocations : op.allocations).map((alloc) => (
                        <tr
                          key={alloc.id}
                          className={`border-b last:border-0 ${isParentOp ? "border-emerald-100" : "border-slate-100"}`}
                        >
                          <td className="py-1 pr-2 tabular-nums text-slate-700 whitespace-nowrap">{alloc.date ?? ""}</td>
                          <td className="py-1 pr-2 text-slate-700">{alloc.label}</td>
                          <td className="py-1 pr-2 text-slate-400 whitespace-nowrap">{alloc.typeBudget ?? ""}</td>
                          <td className="py-1 pr-2 tabular-nums text-right text-slate-700 whitespace-nowrap">
                            {alloc.typeBudget?.toUpperCase() === "STOCK"
                              ? (alloc.stockDebitUnits ? `${alloc.stockDebitUnits} u.` : "")
                              : (alloc.debit ? `${formatAmount(alloc.debit)} \u20AC` : "")}
                          </td>
                          <td className="py-1 tabular-nums text-right text-slate-700 whitespace-nowrap">
                            {alloc.typeBudget?.toUpperCase() === "STOCK"
                              ? (alloc.stockCreditUnits ? `${alloc.stockCreditUnits} u.` : "")
                              : (alloc.credit ? `${formatAmount(alloc.credit)} \u20AC` : "")}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Inline editor for current allocation — only in parent op */}
                {isParentOp && !readOnly && (
                  <div className="rounded-md border border-emerald-300 bg-white p-2.5 space-y-2 [&_label]:!text-[12px] [&_label]:font-normal [&_label]:leading-tight [&_input]:!text-[12px] [&_input]:md:!text-[12px] [&_input]:leading-tight">
                    <div className="text-[11px] font-medium text-emerald-700">Écriture en cours d&apos;édition</div>
                    <div className="grid grid-cols-2 xl:grid-cols-4 gap-2">
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Date</label>
                        <Input
                          type="date"
                          min={ACCOUNTING_DATE_BOUNDS.min}
                          max={ACCOUNTING_DATE_BOUNDS.max}
                          value={simpleEditorRow?.date ?? ""}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "date", event.target.value, selectedAllocation);
                          }}
                          className="h-7 !text-[12px] md:!text-[12px] leading-tight"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-[12px] leading-tight text-slate-600">Type budget</label>
                        <select
                          className="h-7 w-full rounded-md border border-slate-200 bg-white px-2 text-[12px]"
                          value={simpleEditorRow?.budgetType || "ASC"}
                          onChange={(event) => {
                            if (!selectedAllocation) return;
                            setField(selectedAllocation.id, "budgetType", event.target.value, selectedAllocation);
                          }}
                          disabled
                        >
                          <option value="ASC">ASC</option>
                          <option value="AEP">AEP</option>
                          <option value="STOCK">STOCK</option>
                        </select>
                      </div>
                      {(simpleEditorRow?.budgetType ?? "").toUpperCase() === "STOCK"
                        ? (
                          <>
                            <div className="space-y-1">
                              <label className="text-[12px] leading-tight text-slate-600">Débit (unités)</label>
                              <Input
                                inputMode="decimal"
                                value={simpleEditorRow?.stockDebitQuantity ?? ""}
                                onChange={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "stockDebitQuantity", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                                }}
                                className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isUnitsDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                                placeholder="0"
                                readOnly={isUnitsDebitLocked}
                                aria-disabled={isUnitsDebitLocked}
                                title={isUnitsDebitLocked ? DEBIT_UNITS_LOCKED_HINT : undefined}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[12px] leading-tight text-slate-600">Crédit (unités)</label>
                              <Input
                                inputMode="decimal"
                                value={simpleEditorRow?.stockCreditQuantity ?? ""}
                                onChange={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "stockCreditQuantity", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                                }}
                                className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isUnitsCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                                placeholder="0"
                                readOnly={isUnitsCreditLocked}
                                aria-disabled={isUnitsCreditLocked}
                                title={isUnitsCreditLocked ? CREDIT_UNITS_LOCKED_HINT : undefined}
                              />
                            </div>
                          </>
                        )
                        : (
                          <>
                            <div className="space-y-1">
                              <label className="text-[12px] leading-tight text-slate-600">Débit</label>
                              <Input
                                inputMode="decimal"
                                value={simpleEditorRow?.debit ?? ""}
                                onChange={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "debit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                                }}
                                onBlur={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "debit", formatCurrencyField(event.target.value), selectedAllocation);
                                }}
                                className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isDebitLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                                placeholder="0,00"
                                readOnly={isDebitLocked}
                                aria-disabled={isDebitLocked}
                                title={isDebitLocked ? DEBIT_LOCKED_HINT : undefined}
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-[12px] leading-tight text-slate-600">Crédit</label>
                              <Input
                                inputMode="decimal"
                                value={simpleEditorRow?.credit ?? ""}
                                onChange={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "credit", normalizeCurrencyTyping(event.target.value), selectedAllocation);
                                }}
                                onBlur={(event) => {
                                  if (!selectedAllocation) return;
                                  setField(selectedAllocation.id, "credit", formatCurrencyField(event.target.value), selectedAllocation);
                                }}
                                className={`h-7 !text-[12px] md:!text-[12px] leading-tight tabular-nums text-right ${isCreditLocked ? LOCKED_AMOUNT_INPUT_CLASS : ""}`}
                                placeholder="0,00"
                                readOnly={isCreditLocked}
                                aria-disabled={isCreditLocked}
                                title={isCreditLocked ? CREDIT_LOCKED_HINT : undefined}
                              />
                            </div>
                          </>
                        )}
                    </div>
                    {(simpleEditorRow?.budgetType ?? "").toUpperCase() !== "STOCK" && (
                      <AllocationRoomHint
                        operationBalance={op.allocationBalance}
                        allocation={selectedAllocation}
                        disabled={isSimpleEditorBusy}
                        onApply={(debit, credit) => {
                          if (!selectedAllocation) return;
                          setField(selectedAllocation.id, "debit", debit, selectedAllocation);
                          setField(selectedAllocation.id, "credit", credit, selectedAllocation);
                        }}
                      />
                    )}
                    <div className="space-y-1">
                      <label className="text-[12px] leading-tight text-slate-600">Libellé</label>
                      <Input
                        value={simpleEditorRow?.description ?? ""}
                        onChange={(event) => {
                          if (!selectedAllocation) return;
                          setField(selectedAllocation.id, "description", event.target.value, selectedAllocation);
                        }}
                        className="h-7 !text-[12px] md:!text-[12px] leading-tight"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-0.5">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={onClose}
                        disabled={isSimpleEditorBusy}
                        className="h-7 text-[11px] px-3"
                      >
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        variant="success"
                        onClick={onSave}
                        disabled={isSimpleEditorBusy}
                        className="h-7 text-[11px] px-3"
                      >
                        Enregistrer
                      </Button>
                    </div>
                  </div>
                )}

                {/* Empty state for non-parent ops */}
                {!isParentOp && op.allocations.length === 0 && (
                  <div className="text-[11px] italic text-slate-400">Aucune écriture</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </BaseDrawer>
  );
};

export default EcommerceAllocationDetail;
